import { injectable, inject } from 'inversify';
import { Store } from '@openfin/predux';
import { DeferredPromise } from 'openfin-service-async';

import { Inject } from '../common/Injectables';
import { StoredNotification } from '../model/StoredNotification';
import { Database, CollectionMap } from '../model/database/Database';
import { Collection } from '../model/database/Collection';
import { StoredApplication } from '../model/Environment';
import { StoredSetting, SettingsMap } from '../model/StoredSetting';
import { getSourceTitle, getStreamTitle } from '../view/utils/SourceInfo';
import { StoredStream } from '../model/StoredStream';

import { RootState } from './State';
import { initialSettingsState } from './partials/Settings';
import { initialSearchState } from './partials/Search';
import { initialSortedNotificationsState } from './partials/Notification';
import { insertNotificationIntoSortedMaps } from '../utils/sortNotifications';

@injectable()
export class ServiceStore extends Store<RootState> {
  public static readonly INITIAL_STATE: RootState = {
    notifications: [],
    search: initialSearchState,
    stickyNotifications: [],
    sortedNotifications: initialSortedNotificationsState,
    applications: new Map<string, StoredApplication>(),
    centerVisible: false,
    centerLocked: true,
    centerMuted: false,
    settings: initialSettingsState,
    streams: new Map<string, StoredStream>(),
    loggedIn: false,
  };

  private readonly _initialized: DeferredPromise<this>;

  public get initialized(): Promise<this> {
    return this._initialized.promise;
  }

  constructor(@inject(Inject.DATABASE) private readonly database: Database) {
    super(ServiceStore.INITIAL_STATE);
    this._initialized = new DeferredPromise<this>();
  }

  public delayedInit(): Promise<this> {
    this.init().then(() => {
      this._initialized.resolve(this);
    });

    return this._initialized.promise;
  }

  protected async init(): Promise<void> {
    await this.database.initialized;
    this.setState(await this.getInitialState());
  }

  private async getInitialState(): Promise<RootState> {
    const applicationsCollection: Collection<StoredApplication> = this.database.get(
      CollectionMap.APPLICATIONS
    );
    const streamsCollection: Collection<StoredStream> = this.database.get(CollectionMap.STREAMS);
    const applications = new Map<string, StoredApplication>(
      (await applicationsCollection.getAll()).map((application) => [application.id, application])
    );
    const streams = new Map<string, StoredStream>(
      (await streamsCollection.getAll()).map((stream) => [stream.id, stream])
    );

    const notificationCollection: Collection<StoredNotification> = this.database.get(
      CollectionMap.NOTIFICATIONS
    );

    let sortedNotifications = initialSortedNotificationsState;
    let notifications: StoredNotification[] = await notificationCollection.getAll();

    // Load up titles from applications
    notifications = notifications.map((n) => {
      const nft = {
        ...n,
        title:
          getStreamTitle(streams, n.notification.stream?.id) ||
          getSourceTitle(n.source, applications),
      };

      sortedNotifications = insertNotificationIntoSortedMaps(sortedNotifications, nft);

      return nft;
    });

    const settingsCollection: Collection<StoredSetting> = this.database.get(CollectionMap.SETTINGS);
    const storedCenterLocked = await settingsCollection.get(SettingsMap.CenterLocked);
    const storedCenterMuted = await settingsCollection.get(SettingsMap.CenterMuted);
    const storedCenterPosition = await settingsCollection.get(SettingsMap.CenterPosition);
    const storedToastPosition = await settingsCollection.get(SettingsMap.ToastPosition);
    const storedToastDuraction = await settingsCollection.get(SettingsMap.ToastDuration);

    return {
      ...ServiceStore.INITIAL_STATE,
      notifications,
      sortedNotifications,
      applications,
      streams,
      centerLocked: storedCenterLocked
        ? (storedCenterLocked.value as boolean)
        : ServiceStore.INITIAL_STATE.centerLocked,
      centerMuted: storedCenterMuted
        ? (storedCenterMuted.value as boolean)
        : ServiceStore.INITIAL_STATE.centerMuted,
      settings: {
        centerPosition:
          storedCenterPosition?.value ?? ServiceStore.INITIAL_STATE.settings.centerPosition,
        toastPosition:
          storedToastPosition?.value ?? ServiceStore.INITIAL_STATE.settings.toastPosition,
        toastDuration:
          storedToastDuraction?.value ?? ServiceStore.INITIAL_STATE.settings.toastDuration,
      },
    };
  }
}
